<template>
  <div class="calendar-label">
    <h2 @click="toggleMode()">{{ archiveTitle }}</h2>
    <svg
      viewBox="0 0 18 18"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      xml:space="preserve"
      xmlns:serif="http://www.serif.com/"
      class="month-nav"
      :class="{ disabled: this.prevDisabled }"
      @click="changePeriod(-1, currentMode)"
    >
      <path
        d="M16.193,11.309c0.169,0.144 0.229,0.377 0.152,0.585c-0.076,0.208 -0.275,0.346 -0.496,0.346l-14.355,-0c-0.225,0 -0.425,-0.141 -0.499,-0.353c-0.075,-0.211 -0.009,-0.446 0.165,-0.588c2.039,-1.653 6.164,-4.997 7.377,-5.981c0.199,-0.161 0.484,-0.157 0.678,0.009c1.163,0.997 5.06,4.337 6.978,5.982Z"
      />
    </svg>
    <svg
      viewBox="0 0 18 18"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      xml:space="preserve"
      xmlns:serif="http://www.serif.com/"
      class="month-nav"
      :class="{ disabled: this.nextDisabled }"
      @click="changePeriod(1, currentMode)"
    >
      <path
        d="M1.087,5.971c-0.169,-0.144 -0.229,-0.377 -0.152,-0.585c0.076,-0.208 0.275,-0.346 0.496,-0.346l14.355,0c0.225,-0 0.425,0.141 0.499,0.353c0.075,0.211 0.009,0.446 -0.165,0.588c-2.039,1.653 -6.164,4.997 -7.377,5.981c-0.199,0.161 -0.484,0.157 -0.678,-0.009c-1.163,-0.997 -5.06,-4.337 -6.978,-5.982Z"
      />
    </svg>
  </div>

  <transition-group :name="scopeTransitionName" tag="div" id="calendar-wrapper">
    <template v-for="mode in modes">
      <div
        class="calendar-container"
        v-if="mode === currentMode"
        :key="mode"
        :class="mode"
      >
        <div class="calendar-columns calendar-header" v-if="mode === 'month'">
          <div v-for="(day, index) in calendarHeaders" :key="index" class="">
            {{ day }}
          </div>
        </div>
        <div class="calendar-body" >
          <transition :name="calendarTransitionName">
            <div class="calendar-page" :key="archiveKey">
              <div class="calendar-columns calendar-rows">
                <div
                  v-for="day in archiveStartFill"
                  :key="day"
                  class="calendar-cell"
                ></div>
                <div
                  v-for="day in archiveMonthDays"
                  :key="day"
                  class="calendar-cell"
                >
                  <template v-if="mode === 'month'">
                    <template v-if="(statusData = puzzleStatus(day))">
                      <button
                        class="calendar-select single-button"
                        :class="statusData.classes"
                        v-if="hasPuzzle(day)"
                        @click="setPuzzle(day)"
                      >
                        {{ statusData.level }}
                      </button>
                      <div class="calendar-select" v-else>
                        <div></div>
                      </div>
                    </template>
                    <span>{{ day }}</span>
                  </template>
                  <template v-else>
                    <template v-if="(statusData = monthStatus(day))">
                      <button
                        class="calendar-select single-button"
                        :class="statusData.classes"
                        v-if="hasPuzzle(day, false)"
                        @click="setMonth(day)"
                      >
                        {{ monthLabel(day) }}
                      </button>
                      <div class="calendar-select" v-else>
                        <div></div>
                      </div>
                    </template>
                  </template>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </template>
  </transition-group>
</template>

<script>
import { MONTHS, MONTHS_ABBR, MODE } from "../types.js";

function formatDate(year, month, day) {
  // Ensure the month and day are two digits by padding with leading zeros if necessary
  const formattedMonth = String(month).padStart(2, "0");
  const formattedDay = String(day).padStart(2, "0");

  return `${year}-${formattedMonth}-${formattedDay}`;
}

export default {
  name: "KnotArchive",
  emits: ["set-puzzle"],
  props: {
    launchDate: {
      type: Date,
    },
    archiveDate: {
      type: Date,
    },
    today: {
      type: Date,
    },
    archiveHistory: {
      type: Object,
      default: new Object(),
    },
  },
  data() {
    return {
      elapsed: 0,
      timer: null,
      currentDate: null,
      delta: 1,
      calendarHeight: "auto",
      currentMode: "month",
      modes: ["month", "year"],
    };
  },
  created() {
    this.currentDate = new Date(this.archiveDate);
  },
  computed: {
    calendarHeaders() {
      return this.currentMode === "month"
        ? ["S", "M", "T", "W", "T", "F", "S"]
        : Array(4).fill("");
    },
    scopeTransitionName() {
      return this.currentMode === "month"
        ? "calendar-zoom-in"
        : "calendar-zoom-out";
    },
    calendarTransitionName() {
      return this.delta > 0 ? "calendar-slide-up" : "calendar-slide-down";
    },
    archiveKey() {
      return this.archiveYear + "" + this.archiveMonth;
    },
    archiveStartFill() {
      return this.currentMode === "month" ? this.archiveMonthStart.getDay() : 0;
    },
    archiveMonthStart() {
      return new Date(this.currentDate.setDate(1));
    },
    archiveMonthEnd() {
      return new Date(this.archiveYear, this.archiveMonth + 1, 0);
    },
    archiveMonthDays() {
      return this.currentMode === "month" ? this.archiveMonthEnd.getDate() : 12;
    },
    archiveMonth() {
      return this.currentDate.getMonth();
    },
    archiveMonthString() {
      return MONTHS[this.currentDate.getMonth()];
    },
    archiveTitle() {
      return this.currentMode === "month"
        ? `${
            MONTHS[this.currentDate.getMonth()]
          } ${this.currentDate.getFullYear()}`
        : this.currentDate.getFullYear();
    },
    archiveYear() {
      return this.currentDate.getFullYear();
    },
    prevDisabled() {
      return this.currentMode === "month"
        ? this.archiveMonthStart <= this.launchDate
        : this.archiveMonthStart.getFullYear() <= this.launchDate.getFullYear();
    },
    nextDisabled() {
      return this.currentMode === "month"
        ? this.archiveMonthEnd >= this.today
        : this.archiveMonthEnd.getFullYear() >= this.today.getFullYear();
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.setCalendarHeight();
    });
  },
  methods: {
    monthLabel(monthIndex) {
      return MONTHS_ABBR[monthIndex - 1];
    },
    toggleMode() {
      this.currentMode = this.currentMode === "month" ? "year" : "month";
    },
    setCalendarHeight() {
      const calendar = this.$refs.calendarContainer;
      if (calendar) {
        this.calendarHeight = calendar.offsetHeight + "px";
      }
    },
    monthStatus(month) {
      const monthDate = new Date(this.archiveYear, month - 1, 1).getTime();
      const archiveDate = new Date(
        this.archiveDate.getFullYear(),
        this.archiveDate.getMonth(),
        1
      ).getTime();
      let result = {
        classes: {
          "selected-date": monthDate === archiveDate,
          "not-started": true,
        },
      };
      return result;
    },
    puzzleStatus(day) {
      const puzzleDayStr = formatDate(
        this.archiveYear,
        this.archiveMonth + 1,
        day
      );
      const puzzleDate = new Date(
        this.archiveYear,
        this.archiveMonth,
        day
      ).setHours(0, 0, 0, 0);
      let result = {
        level: null,
        active: puzzleDate === this.archiveDate.getTime(),
        classes: {
          "not-started": true,
          "selected-date": puzzleDate === this.archiveDate.getTime(),
        },
      };
      if (this.archiveHistory !== null) {
        if (puzzleDayStr in this.archiveHistory) {
          result.level = this.archiveHistory[puzzleDayStr].level;
          result.classes = {
            "hard-mode":
              this.archiveHistory[puzzleDayStr].mode === MODE["HARD"],
            "in-progress": !this.archiveHistory[puzzleDayStr].isComplete,
            "selected-date": puzzleDate === this.archiveDate.getTime(),
          };
        }
      }
      return result;
    },
    changePeriod(delta, mode) {
      this.delta = delta;
      if (delta < 0 && this.prevDisabled) {
        return;
      } else if (delta > 0 && this.nextDisabled) {
        return;
      }
      let yearDelta = 0;
      let monthDelta = 0;
      if (mode === "month") {
        monthDelta = delta;
      } else {
        yearDelta = delta;
      }
      const currentDate = new Date(
        this.archiveYear + yearDelta,
        this.archiveMonth + monthDelta,
        1
      );
      if (currentDate < this.launchDate) {
        this.currentDate = new Date(this.launchDate);
      } else if (currentDate > this.today) {
        this.currentDate = new Date(this.today);
      } else {
        this.currentDate = currentDate;
      }
    },
    hasPuzzle(input, day = true) {
      let puzzleDay;
      let launchDate;
      if (day) {
        puzzleDay = new Date(this.archiveYear, this.archiveMonth, input);
        launchDate = this.launchDate;
      } else {
        puzzleDay = new Date(this.archiveYear, input - 1, 1);
        launchDate = new Date(
          this.launchDate.getFullYear(),
          this.launchDate.getMonth(),
          -1
        ).getTime();
      }
      return puzzleDay > launchDate && puzzleDay <= this.today;
    },
    setMonth(month) {
      this.currentDate = new Date(this.archiveYear, month - 1, 1);
      this.currentMode = "month";
    },
    setPuzzle(day) {
      return this.$parent.$parent.$emit(
        "set-puzzle",
        new Date(this.archiveYear, this.archiveMonth, day),
        true
      );
    },
  },
};
</script>

<style scoped>
h2 {
  text-align: center;
}
a {
  cursor: pointer;
}
#calendar-wrapper {
  position: relative;
}
.calendar-container {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
}
.calendar-page {
  padding: 0 0.75vh 2vh;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
}

.calendar-body {
  overflow: hidden;
  position: relative;
  height: 60.5vh
}

.calendar-columns,
.calendar-rows {
  display: grid;
  gap: 0.65vh;
  font-size: 1.125em;
}

.calendar-page {
  padding: 2vh 0;
}
.calendar-header {
  border-bottom: 2px solid var(--border-color);
  font-weight: bold;
  text-align: center;
  display: grid;
  height: 3.5vh;
  align-content: center;
}

.month .calendar-columns {
  grid-template-columns: repeat(7, 1fr);
  padding-left: 0.75vh;
  padding-right: 0.75vh;
}

.year .calendar-columns {
  grid-template-columns: repeat(4, 1fr);
}

.month .calendar-rows {
  grid-template-rows: repeat(6, 1fr);
}

.year .calendar-rows {
  grid-template-rows: repeat(3, 1fr);
}

.calendar-cell {
  text-align: center;
  display: flex;
  flex-direction: column;
  grid-gap: var(--key-spacing);
}

.month .calendar-select {
  aspect-ratio: 1/1;
}
.year .calendar-select {
  aspect-ratio: 4/5;
}
.calendar-select {
  border-radius: 10%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: inherit;
}
button.calendar-select {
  border: 1px solid var(--border-color);
  font-size: 3vh;
  color: var(--background-color);
  background-color: var(--border-color);
  position: relative;
}
button.not-started {
  background-color: var(--background-color);
}
button.in-progress {
  background-color: var(--in-progress-color);
}
.calendar-day span {
  font-weight: bold;
}
.calendar-label {
  display: flex;
  grid-gap: 1vh;
}
.calendar-label h2 {
  text-align: left;
  cursor: pointer;
  flex: 1;
}
.calendar-select div {
  width: 2vh;
  height: 2vh;
  border-radius: 50%;
  background-color: var(--border-color);
  opacity: 50%;
}
.year button.calendar-select {
  color: var(--border-color);
}

.hard-mode::before {
  content: "*";
  position: absolute;
  top: 0;
  font-size: 0.5em;
  font-weight: normal;
}
.month-nav {
  aspect-ratio: 1/1;
  height: 2.5vh;
  cursor: pointer;
  fill: var(--border-color);
}
.selected-date {
  animation: 1s hover infinite step-end;
}

.month-nav.disabled {
  stroke: var(--disabled);
  fill: #999;
  cursor: auto;
}

@keyframes hover {
  50% {
    box-shadow: 0px 0px 0px 3px var(--border-color);
    -webkit-box-shadow: 0px 0px 0px 3px var(--border-color);
    -moz-box-shadow: 0px 0px 0px 3px var(--border-color);
  }
}

.calendar-slide-down-enter-active,
.calendar-slide-down-leave-active,
.calendar-slide-up-enter-active,
.calendar-slide-up-leave-active,
.calendar-zoom-out-enter-active,
.calendar-zoom-out-leave-active,
.calendar-zoom-in-enter-active,
.calendar-zoom-in-leave-active {
  transition: all 0.5s ease;
}

/* Forward direction (new month, slide up) */

.calendar-slide-up-enter-from {
  opacity: 0;
  transform: translateY(100%);
}
.calendar-slide-up-enter-to {
  opacity: 1;
}
.calendar-slide-up-leave-from {
  opacity: 1;
}
.calendar-slide-up-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}

/* Backward direction (previous month, slide down) */

.calendar-slide-down-enter-from {
  opacity: 0;
  transform: translateY(-100%);
}
.calendar-slide-down-enter-to {
  opacity: 1;
}
.calendar-slide-down-leave-from {
  opacity: 1;
}
.calendar-slide-down-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

/* Zoom out direction */
.calendar-zoom-out-enter-from {
  opacity: 0;
  transform: scale(2);
}
.calendar-zoom-out-enter-to {
  opacity: 1;
}

.calendar-zoom-out-leave-from {
  opacity: 1;
}
.calendar-zoom-out-leave-to {
  opacity: 0;
  transform: scale(0.5);
}

/* Zoom in direction */
.calendar-zoom-in-enter-from {
  opacity: 0;
  transform: scale(0.5);
}
.calendar-zoom-in-enter-to {
  opacity: 1;
}

.calendar-zoom-in-leave-from {
  opacity: 1;
}
.calendar-zoom-in-leave-to {
  opacity: 0;
  transform: scale(2);
}
</style>
